import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { InventoryClassCodeEnums, InventoryCodeEnums } from 'src/app/enum/inventory-enums';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { InventoryModel } from 'src/app/models/inventory/inventory.model';
import { UserService } from 'src/app/services/authentication/user.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InventoryService } from 'src/app/services/inventory/inventory.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { InventoryUserComponent } from '../../inventory-user/inventory-user.component';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-register-inventory',
  templateUrl: './register-inventory.component.html',
  styleUrls: ['./register-inventory.component.scss'],
  standalone: false
})
export class RegisterInventoryComponent implements OnInit {

  @ViewChild('inventoryUserModal') inventoryUserModalComponent?: CustomModalComponent | null = null;
  componentInventoryUserModal = InventoryUserComponent;

  companyId: number = 0;
  disabledForm: boolean = false;
  formInventory: FormGroup = new FormGroup({});
  inventoryContract: InventoryModel = new InventoryModel();
  inventoryId: number = 0;
  isASADMIN: boolean = false;
  isOpened: string | undefined = undefined;
  inputsForInventoryUserModals$ = new BehaviorSubject<any>({
    inventoryName: '',
    inventoryId: 0
  });
  isButtonDisabled: boolean = false;
  isInventorySubrental: boolean = false;
  listClassInventories: CustomListDropdown[] = [];
  listTypeInventories: CustomListDropdown[] = [];
  nameWindow: string = 'Inventarios';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = 'inventory';

  constructor(private activatedRoute: ActivatedRoute,
    private confirmDialogService: ConfirmDialogService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private inventoryService: InventoryService,
    private location: Location,
    private router: Router,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private userService: UserService,
    private windowService: WindowService) {
    this.validateForm(new InventoryModel());
  }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    await this.userAuthenticatedIsASADMIN();
    this.activatedRoute.params.subscribe(params => {
      this.inventoryId = Object.keys(params).length > 0 ? params?.id : null;
    });
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeInventory() {
    const code = this.formInventory.get("code")?.value;
    if (code != '' && code >= 0) {
      await this.getInventoryByCode(code);
    } else {
      this.createNewRegister();
      this.toastHandlerService.showToast(
        'error',
        "El inventario ingresado NO existe. Para crear uno nuevo, presione el botón 'Nuevo'"
      );
    }
  }

  createNewRegister() {
    if (this.isASADMIN && this.permissionUpdatable) {
      this.inventoryId = 0;
      this.inventoryContract = new InventoryModel();
      this.validateForm(this.inventoryContract);
      this.location.replaceState(`/${this.url}/register`);
      setTimeout(() => this.formInventory.controls["code"].disable());
    } else {
      this.updateFormPermision();
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para crear inventarios. Comuníquese con el administrador del sistema.'
      );
      return;
    }
  }

  async deleteInventory() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para eliminar inventarios. Comuníquese con el administrador del sistema.'
      );
      return;
    }

    if (this.isInventorySubrental) {
      this.toastHandlerService.showToast(
        'error',
        'No se puede eliminar el inventario principal de subalquiler.'
      );
      return;
    }

    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el inventario?')) {
      try {
        await firstValueFrom(this.inventoryService.deleteInventory(this.inventoryContract.inventoryId));
        this.toastHandlerService.showToast(
          'success',
          'El inventario fue eliminado exitosamente.'
        );
        this.createNewRegister();
      } catch (error) {
        console.error('Error al eliminar el inventario.', error);
      }
    }
  }

  async getAllLists() {
    try {
      const [typeInventories, classInventories] = await Promise.all([
        firstValueFrom(this.inventoryService.getAllTypeInventoriesForSelectAsync()),
        firstValueFrom(this.inventoryService.getAllInventoryClassForSelectAsync()),
      ]);
      this.listTypeInventories = typeInventories;
      this.listClassInventories = classInventories;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas.', error);
      return false;
    }
  }

  async getInventoryByCode(code: number) {
    try {
      const response = await firstValueFrom(this.inventoryService.getInventoryByCode(code));
      this.inventoryId = response.inventoryId;
      this.inventoryContract = response;
      this.validateForm(this.inventoryContract);
    } catch (error) {
      this.createNewRegister();
      console.error('Error al obtener inventario por código.', error);
    }
  }

  async getInventoryById() {
    try {
      const response = await firstValueFrom(this.inventoryService.getInventoryById(this.inventoryId));
      this.inventoryContract = response;
      this.validateForm(this.inventoryContract);
    } catch (error) {
      console.error('Error al obtener el inventario.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'warning',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await this.getPermissionUpdatable(this.url);
  }

  async getPermissionUpdatable(url: string) {
    if (this.isASADMIN) {
      this.permissionUpdatable = true;
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(url));
    } catch (error) {
      console.error('Error al obtener el perfil de permisos por URL.', error);
    }
  }

  goToBack(event: Event) {
    event.preventDefault();
    this.router.navigate([`/inventory`]);
  }

  async handleInitialState() {
    if (this.inventoryId !== null && this.inventoryId !== 0) {
      await this.getInventoryById();
    } else {
      this.createNewRegister();
    }
  }

  openModal() {
    this.inputsForInventoryUserModals$.next({
      inventoryId: this.inventoryContract.inventoryId,
      inventoryName: this.inventoryContract.name
    });

    this.inventoryUserModalComponent?.show();
  }

  async send() {
    if (this.formInventory.valid) {
      const modelInventory = this.formInventory.getRawValue() as InventoryModel;
      if (this.inventoryId == 0) {
        try {
          const response = await firstValueFrom(this.inventoryService.createInventory(modelInventory));
          this.toastHandlerService.showToast(
            'success',
            'El inventario fue creado exitosamente.'
          );
          this.inventoryId = response.inventoryId;
          this.inventoryContract = response;
          this.validateForm(this.inventoryContract);
          this.location.replaceState(`/${this.url}/register/${this.inventoryId}`);
          setTimeout(() => this.formInventory.controls["code"].enable());
        } catch (error) {
          console.error('Error al crear el inventario.', error);
        }
      } else {
        this.inventoryContract.code = modelInventory.code;
        this.inventoryContract.name = modelInventory.name;
        this.inventoryContract.typeInventoryId = modelInventory.typeInventoryId;
        this.inventoryContract.inventoryClassId = modelInventory.inventoryClassId;
        this.inventoryContract.localCosting = modelInventory.localCosting;
        this.inventoryContract.swInternalOrder = modelInventory.swInternalOrder;
        try {
          const response = await firstValueFrom(this.inventoryService.updateInventory(this.inventoryContract));
          this.inventoryContract = response;
          this.validateForm(this.inventoryContract);
          this.toastHandlerService.showToast(
            'success',
            'El inventario fue modificado exitosamente.'
          );
        } catch (error) {
          console.error('Error al actualizar el inventario.', error)
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      code: {
        required: 'El campo Código es obligatorio.'
      },
      name: {
        required: 'El campo Nombre es obligatorio.',
        maxlength: 'El campo Nombre no puede superar los 500 caracteres.'
      },
      typeInventoryId: {
        required: 'El campo Tipo de Inventario es obligatorio.',
        min: 'Debe seleccionar un Tipo de Inventario válido.'
      },
      inventoryClassId: {
        required: 'El campo Clase de Inventario es obligatorio.',
        min: 'Debe seleccionar una Clase de Inventario válida.'
      },
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formInventory.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormPermision() {
    this.isInventorySubrental =
      this.inventoryId > 0 && this.inventoryContract.code == InventoryCodeEnums.subrental &&
      this.inventoryContract.inventoryClassId === InventoryClassCodeEnums.subrental;

    if (this.isInventorySubrental) {
      this.disabledForm = true;
      this.isButtonDisabled = !this.permissionUpdatable;
      setTimeout(() => {
        this.formInventory.disable();
        this.formInventory.controls['code'].enable();
      });
      return;
    }
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      this.isButtonDisabled = this.inventoryId === 0;
      setTimeout(() => this.formInventory.enable());
    } else {
      this.disabledForm = true;
      this.isButtonDisabled = true;
      setTimeout(() => this.formInventory.disable());
      setTimeout(() => this.formInventory.controls["code"].enable());
    }
  }

  async userAuthenticatedIsASADMIN() {
    try {
      const response = await firstValueFrom(this.userService.userAuthenticatedIsASADMIN());
      this.isASADMIN = response;
      await Promise.all([
        this.getAllLists(),
        this.getPermission()
      ]);
    } catch (error) {
      console.error('Error al validar si el usuario es ASADMIN.', error);
    }
  }

  validateForm(inventory: InventoryModel) {
    this.formInventory = this.formBuilder.group({
      code: [inventory.code, [Validators.required]],
      name: [inventory.name, [Validators.required, Validators.maxLength(500)]],
      typeInventoryId: [inventory.typeInventoryId, [Validators.required, Validators.min(1)]],
      inventoryClassId: [inventory.inventoryClassId, [Validators.required, Validators.min(1)]],
      localCosting: [inventory.localCosting],
      swInternalOrder: [inventory.swInternalOrder]
    });
    this.updateFormPermision();
  }
}
